/**
 * Générateur — Automatisme : 1 cm² = 1/100 dm² = 0,01 dm²
 * Attendu ID : 1029 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève mémorise que 1 cm²
 * est égal à un centième de 1 dm², qu'il écrit 1 cm² = 1/100 dm² ou
 * 1 cm² = 0,01 dm². »
 * Labels (cartes exerciseur) :
 *   - 1 cm², un centième de dm²
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * PÉRIMÈTRE STRICT : la paire dm² ↔ cm² SEULEMENT, dans le sens descendant
 * (la réciproque du gen_1028 : là « 1 dm² contient 100 cm² », ici « 1 cm²
 * vaut un centième de dm² »). UNE relation, TROIS écritures : en mots
 * (« un centième »), en fraction (1/100), en décimal (0,01).
 * L'ERREUR CIBLE : 1 cm² = 0,1 dm² — l'analogie avec 1 cm = 0,1 dm.
 * Domaine minuscule assumé : tirage SANS REMISE par format (cf. gen_1028),
 * et rotation des réponses (1/3 des QCM : l'unité ou le 1 en réponse ;
 * saisie : trous 0,01 / 1 / 100 selon le gabarit — jamais « toujours 0,01 »).
 * Jamais le symbole ÷, jamais de manipulation d'écriture, jamais m² en
 * réponse juste (m² ne sert que de leurre d'unité).
 */
import {
    randomChoice, shuffle, tirageSansRemise,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1029;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = '1 cm², un centième de dm²';

const FRAC = (q) => `$\\dfrac{1}{${q}}$`;

// ─── Format 1 : QCM (trois écritures + rotation unité / le 1) ─────────────

/** Assemble le payload QCM avec sousType posé dans contenu (routage cdp). */
const finirQcm = (bonne, dis, texte, sousType) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

/** 1/3 des tirages : la réponse n'est ni 0,01 ni « un centième ». */
const genQcmRotation = () => {
    if (Math.random() < 0.5) {
        // l'UNITÉ en réponse — 3 leurres tirés parmi 4 (grilles tournantes)
        const dis = shuffle([
            { valeur: 'cm²', code: 'recopie_unite' },
            { valeur: 'dm', code: 'oubli_du_carre' },
            { valeur: 'm²', code: 'mauvaise_paire' },
            { valeur: 'cm', code: 'oubli_du_carre' },
        ]).slice(0, 3);
        return finirQcm('dm²', dis, '<p><b>Complète : 1 cm² = 0,01 ...</b></p>', 'unite');
    }
    // le 1 en réponse : « 0,01 dm², c'est exactement UN cm² » — deux sens
    const texte = Math.random() < 0.5
        ? '<p><b>Complète : 0,01 dm² = ... cm²</b></p>'
        : '<p><b>Complète : ... cm² = 0,01 dm²</b></p>';
    const dis = [
        { valeur: '100', code: 'sens_inverse' },
        ...shuffle([
            { valeur: '10', code: 'transposition_longueur' },
            { valeur: '0,01', code: 'recopie_nombre' },
            { valeur: '0,1', code: 'transposition_longueur' },
        ]).slice(0, 2),
    ];
    return finirQcm('1', dis, texte, 'un');
};

const genQcm = () => {
    if (Math.random() < 1 / 3) return genQcmRotation();
    const sousType = randomChoice(['decimal', 'mots', 'fraction', 'pavage']);
    // l'erreur cible (le dixième, analogie 1 cm = 0,1 dm) est TOUJOURS là ;
    // les 2 autres pièges tournent parmi 3 (rang de trop, sens inverse,
    // saut double — le symétrique du 10 000 vécu sur le 1028)
    const grilles = {
        decimal: {
            bonne: '0,01',
            cible: { valeur: '0,1', code: 'transposition_longueur' },
            tournants: [
                { valeur: '0,001', code: 'rang_de_trop' },
                { valeur: '100', code: 'sens_inverse' },
                { valeur: '0,0001', code: 'saut_double' },
            ],
        },
        mots: {
            bonne: 'un centième',
            cible: { valeur: 'un dixième', code: 'transposition_longueur' },
            tournants: [
                { valeur: 'un millième', code: 'rang_de_trop' },
                { valeur: 'cent', code: 'sens_inverse' },
                { valeur: 'un dix-millième', code: 'saut_double' },
            ],
        },
        fraction: {
            bonne: FRAC(100),
            cible: { valeur: FRAC(10), code: 'transposition_longueur' },
            tournants: [
                { valeur: FRAC(1000), code: 'rang_de_trop' },
                { valeur: '100', code: 'sens_inverse' },
                { valeur: FRAC(10000), code: 'saut_double' },
            ],
        },
        pavage: {
            // le comptage en question directe (demande prof 2026-08-22) —
            // la réponse est 100, encore une autre valeur dans le cycle
            bonne: '100',
            cible: { valeur: '10', code: 'transposition_longueur' },
            tournants: [
                { valeur: '1 000', code: 'rang_de_trop' },
                { valeur: '0,01', code: 'sens_inverse' },
                { valeur: '10 000', code: 'saut_double' },
            ],
        },
    };
    const g = grilles[sousType];
    const dis = [g.cible, ...shuffle(g.tournants).slice(0, 2)];
    // l'égalité s'écrit dans les deux sens (mots et pavage : question unique)
    let texte;
    if (sousType === 'mots') texte = '<p><b>Quelle part de 1 dm² occupe 1 cm² ?</b></p>';
    else if (sousType === 'pavage') texte = '<p><b>Combien faut-il de carrés de 1 cm² pour faire 1 dm² ?</b></p>';
    else {
        texte = Math.random() < 0.5
            ? '<p><b>Complète : 1 cm² = ... dm²</b></p>'
            : '<p><b>Complète : ... dm² = 1 cm²</b></p>';
    }
    return finirQcm(g.bonne, dis, texte, sousType);
};

// ─── Format 2 : V/F (pool fermé étiqueté à la main) ───────────────────────

// Le pool porte 5 faits pour 9 écritures : deux lignes d'un même fait
// (miroir, ou décimal/fraction) peuvent cohabiter dans un tirage (72 % des
// cas). ARBITRAGE PROF (revue 2026-08-22) : assumé — lire l'égalité dans
// les deux sens et sous ses différentes écritures fait partie de l'attendu.
const AFFIRMATIONS = [
    { t: '1 cm² = 0,01 dm²', v: true },
    { t: `1 cm² = ${FRAC(100)} dm²`, v: true },
    { t: '0,01 dm² = 1 cm²', v: true },
    { t: '1 dm² = 100 cm²', v: true },                  // le pont avec le 1028
    { t: '1 cm² = 0,1 dm²', v: false },                 // L'ERREUR CIBLE
    { t: `1 cm² = ${FRAC(10)} dm²`, v: false },
    { t: '1 cm² = 0,001 dm²', v: false },
    { t: '0,1 dm² = 1 cm²', v: false },
    { t: '100 dm² = 1 cm²', v: false },                 // le sens mal lu
];

const genVraiFaux = () => {
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle([...AFFIRMATIONS]).slice(0, 4);
        const nbVraies = tirage.filter((a) => a.v).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            tirage.map((a) => a.t),
            tirage.map((a) => (a.v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        [AFFIRMATIONS[0].t, AFFIRMATIONS[4].t, AFFIRMATIONS[3].t, AFFIRMATIONS[8].t],
        [0, 1, 0, 1],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 3 : trous saisie (égalités trois écritures, ou comptage) ──────

/** Les trois familles d'égalités à un trou — les réponses varient (0,01 /
 *  1 / 100) pour casser le réflexe « je tape toujours 0,01 ». TOUTES les
 *  lignes sont ENTIÈREMENT en math, trous hors \text (piège vécu 1011 puis
 *  revécu ici : une ligne texte mêlée à une ligne math n'est pas rendue) ;
 *  fraction : trou au dénominateur (triple accolade). */
const FAMILLES_EGALITE = [
    () => (Math.random() < 0.5
        ? '$1\\ \\text{cm}^2 = {{0,01}}\\ \\text{dm}^2$'
        : '${{0,01}}\\ \\text{dm}^2 = 1\\ \\text{cm}^2$'),
    () => (Math.random() < 0.5
        ? '$0{,}01\\ \\text{dm}^2 = {{1}}\\ \\text{cm}^2$'
        : '${{1}}\\ \\text{cm}^2 = 0{,}01\\ \\text{dm}^2$'),
    () => '$1\\ \\text{cm}^2 = \\dfrac{1}{{{100}}}\\ \\text{dm}^2$',
];

const genTrouSaisie = () => {
    const variante = randomChoice(['egalites', 'egalites', 'comptage']);
    if (variante === 'egalites') {
        // 1 ou 2 lignes, familles DISTINCTES (réponses différentes)
        const familles = shuffle([0, 1, 2]).slice(0, randomChoice([1, 2, 2]));
        const lignes = familles.map((i) => FAMILLES_EGALITE[i]());
        return makeTexteTrou(
            lignes.join('\n'),
            '<p><b>Complète les égalités.</b></p>',
            { ...opts(L1), mode: 'saisie' },
        );
    }
    // le comptage : une case sur cent, donc un centième (une idée par ligne)
    const phrase = 'Dans 1 dm², il y a {{100}} carrés de 1 cm² ;\n'
        + '1 cm², c\'est une case sur {{100}}, donc\n1 cm² = {{0,01}} dm²';
    return makeTexteTrou(
        phrase,
        '<p><b>Complète le comptage.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'PÉRIMÈTRE STRICT : dm² ↔ cm² seulement, sens descendant (la réciproque du gen_1028)',
        'les TROIS écritures du programme : « un centième », 1/100, 0,01',
        'l\'erreur cible 0,1 (analogie 1 cm = 0,1 dm) est présente dans toutes les grilles principales',
        'domaine fermé assumé : tirage sans remise + rotation des réponses',
    ],
    centieme_qcm_unique: {
        support: 'l\'égalité (décimale, fractionnaire ou en mots), sur l\'unique relation',
        fixes: {
            transposition_longueur: '0,1 / un dixième / 1/10 — L\'ERREUR CIBLE, toujours présente',
        },
        tournants_2_parmi_3: {
            rang_de_trop: '0,001 / un millième / 1/1000',
            sens_inverse: '100 / cent : la réponse du 1028 recyclée à contre-sens',
            saut_double: '0,0001 / un dix-millième / 1/10000 : deux crans (symétrique du 10 000 du 1028)',
        },
        sens: 'les égalités décimale et fractionnaire s\'écrivent dans les deux sens (... dm² = 1 cm²)',
        rotation: {
            part: '1/3 des tirages : l\'unité (dm² ; leurres cm², dm, m²) ou le 1 (0,01 dm² = ... cm²)',
            fixes_unite: {
                pool: '3 leurres parmi 4 : cm² (recopie), dm et cm (oubli du ² — le lien avec le 1026), m² (un cran trop loin, leurre seulement, jamais réponse juste)',
            },
            fixes_un: {
                pool: '100 (sens inverse) toujours + 2 parmi {10 ; 0,1 (transpositions) ; 0,01 (recopie)} ; deux sens d\'écriture',
            },
        },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    centieme_qcm_unique: genQcm,
    centieme_tableau_vf: genVraiFaux,
    centieme_texte_trou_saisie: genTrouSaisie,
};

const FORMAT_LABELS = {
    centieme_qcm_unique: 'QCM',
    centieme_tableau_vf: 'Vrai/Faux',
    centieme_texte_trou_saisie: 'Saisie',
};

export const CATEGORIES = [
    {
        id: 'centieme',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    // domaine minuscule (1 relation) : TIRAGE SANS REMISE par format —
    // toutes les questions uniques sortent avant qu'une seule ne revienne.
    // Signature = la QUESTION (énoncé + phrase/lignes) + la BONNE RÉPONSE :
    // « 1 cm² = ... dm² » en décimal et en fraction partagent le même texte
    // mais sont deux questions distinctes du cycle.
    const payload = tirageSansRemise(
        `gen_1029:${key}`,
        () => GENERATORS[key](),
        (p) => p.texte
            + JSON.stringify(p.contenu.phrase
                || (p.elements_additionnels?.elements?.[0]?.config?.lignes ?? ''))
            + JSON.stringify(p._meta.params?.sousType ?? ''),
        250,     // certaines combinaisons saisie sont rares (~2 %) : la fin
        //          de cycle a besoin de plus d'essais pour les retrouver
    );
    payload._meta.generatorKey = key;
    return payload;
};
